import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box, Button, Label, SrOnly, Stamp, Text, getPaletteColor } from 'pcln-design-system'

import CheckboxFilter from '../../atoms/CheckboxFilter'
import ChipFilter from '../../atoms/ChipFilter'
import LightboxToolTip from '../../atoms/LightboxToolTip'
import RangeSlider from '../../atoms/RangeSlider'
import RangeSliderNumberInput from '../../atoms/RangeSliderNumberInput'
import RangeSliderLabel from '../../atoms/RangeSliderLabel'

import { getValueTextFormatter, getAriaLabels } from '../../../helpers/getAriaAttributesForSlider'
import { includeSliderContentPropType, markedValuesPropType } from '../../../helpers/commonPropTypes'

import { getUpdatedPriceGuidanceFilter } from './helpers/getUpdatedPriceGuidanceFilter'

const propTypes = {
  avgRetailPrice: PropTypes.number,
  className: PropTypes.string,
  filterName: PropTypes.string,
  filters: PropTypes.arrayOf(PropTypes.object),
  handleChange: PropTypes.func,
  hideCloseIcon: PropTypes.bool,
  includeSliderContent: includeSliderContentPropType,
  includeSliderHeadline: PropTypes.string,
  markedValues: markedValuesPropType,
  priceGuidanceAllStarAvgPrice: PropTypes.number,
  priceGuidanceFilterData: PropTypes.arrayOf(PropTypes.object),
  priceGuidanceNewLabelStyle: PropTypes.bool,
  showChip: PropTypes.bool,
  showIncrementalSlider: PropTypes.bool,
  showSliderInputs: PropTypes.bool,
  sliderInputMaxLabel: PropTypes.string,
  sliderInputMinLabel: PropTypes.string,
  hideCheckboxes: PropTypes.bool,
  theme: PropTypes.shape({
    colors: PropTypes.object.isRequired,
  }).isRequired,
  zIndex: PropTypes.number,
}

const SliderCategoryGroupWrapper = styled.div``

const StyledStamp = styled(Stamp)`
  margin-top: -4px;
  letter-spacing: 0.1em;
  padding-right: 2px;
  font-weight: 500;
`

const CheckBoxFilterBox = styled(Box)`
  ${(props) =>
    props.isDisabled
      ? `> label[for^='price-guidance-'] {
    cursor: not-allowed !important;
     svg {
      cursor: not-allowed !important;
    }
  }`
      : ''}
`

const StyledContentBox = styled(Box)`
  display: ${(props) => (props.isClosed ? 'none' : 'block')};
  position: absolute;
`

const StyledPriceGuidanceButton = styled(Button)`
  font-size: ${(props) => (props.showChip ? props.theme.fontSizes[0] : props.theme.fontSizes[1])}px;
  font-weight: ${(props) => (props.showChip ? 'bold' : '')};
  color: ${getPaletteColor('base')};
  &:hover {
    color: ${getPaletteColor('dark')};
    text-decoration: none;
  }
`
const IncludeSliderContainer = styled(Box)`
  position: ${(props) => (props.containerPosition ? props.containerPosition : 'inherit')};
  ${({ showChip }) =>
    showChip &&
    `text-align: center;
    `}
`

const FilterOptionsBox = styled(Box)`
  ${({ showChip }) =>
    showChip &&
    `display: flex;
    justify-content: center;
    flex-wrap: wrap;
    `}
`

class SliderCategoryGroup extends Component {
  constructor(props) {
    super(props)
    this.filter = this.props.filters[0]
    this.state = {
      filter: this.props.filters,
      min: this.filter.defaultValueArray[0],
      max: this.filter.defaultValueArray[1] || this.filter.max,
      priceGuidanceAllStarAvgPrice: this.props.priceGuidanceAllStarAvgPrice,
      isModalClosed: true,
      priceGuidanceFilterData: [],
    }
    this._handleChange = this.handleChange.bind(this)
    this._handleAfterChange = this.handleAfterChange.bind(this)
    this._handleInputChange = this.handleInputChange.bind(this)
    this._toggleContentModal = this.toggleContentModal.bind(this)
  }

  componentDidUpdate(prevProps) {
    const { priceGuidanceAllStarAvgPrice } = this.props
    if (
      prevProps.priceGuidanceAllStarAvgPrice !== priceGuidanceAllStarAvgPrice ||
      prevProps.priceGuidanceFilterData !== this.props.priceGuidanceFilterData
    ) {
      this.setState(() => {
        return { priceGuidanceFilterData: this.props.priceGuidanceFilterData }
      })
    }
  }

  generateStyledLabel(labelStr) {
    let outputNode = null
    switch (labelStr) {
      case '$':
        outputNode = (
          <React.Fragment>
            <Text.span fontWeight='bold' color='text.base'>
              $
            </Text.span>
            <Text.span color='text.light'>$$$</Text.span>
          </React.Fragment>
        )
        break
      case '$$':
        outputNode = (
          <React.Fragment>
            <Text.span fontWeight='bold' color='text.base'>
              $$
            </Text.span>
            <Text.span color='text.light'>$$</Text.span>
          </React.Fragment>
        )
        break
      case '$$$':
        outputNode = (
          <React.Fragment>
            <Text.span fontWeight='bold' color='text.base'>
              $$$
            </Text.span>
            <Text.span color='text.light'>$</Text.span>
          </React.Fragment>
        )
        break
      case '$$$$':
        outputNode = (
          <Text.span fontWeight='bold' color='text.base'>
            $$$$
          </Text.span>
        )
        break
    }

    return outputNode
  }

  componentDidMount() {
    this.setState(() => {
      return { priceGuidanceFilterData: this.props.priceGuidanceFilterData }
    })
  }

  snapToIncrementOf10 = ({ min, max }) => {
    const minDiff = min % 10
    const maxDiff = max % 10
    if (minDiff || maxDiff) {
      const newMin = Math.round(min / 10) * 10
      const newMax = Math.round(max / 10) * 10
      this.setState({
        min: newMin,
        max: newMax,
      })
    }
  }

  handleChange(value) {
    const { showIncrementalSlider } = this.props
    if (this.filter.oneWay) {
      this.setState(() => {
        return {
          min: value,
        }
      })
    } else {
      if (showIncrementalSlider) {
        this.snapToIncrementOf10({
          min: value[0],
          max: value[1],
        })
      } else {
        this.setState(() => {
          return {
            min: value[0],
            max: value[1],
          }
        })
      }
    }
  }

  handleInputChange(newValues) {
    if (Array.isArray(newValues) && newValues.length === 2) {
      this.setState({ min: newValues[0], max: newValues[1] }, this.handleAfterChange)
    }
  }

  /**
   * Handles changes to checkboxes. When a checkbox value changes, updates min/max value on state
   * if necessary, which causes the range slider to update.
   * @param event
   */
  onChange(event) {
    const targetId = event.target.id
    const isChecked = event.target.checked

    let min
    let max
    const { priceGuidanceFilterData } = this.state
    const priceGuidanceFilterList = priceGuidanceFilterData.map((item) => {
      if (item.id === targetId) {
        item.checked = isChecked
      }
      return item
    })
    priceGuidanceFilterList.forEach((item) => {
      if (item.checked) {
        min = typeof min === 'undefined' ? item.min : min
        max = item.max
      }
    })
    // istanbul ignore else
    if (typeof min !== 'undefined') {
      priceGuidanceFilterList.map((item) => {
        if (item.min > min && item.max < max) {
          item.checked = true
          item.disabled = true
        } else {
          item.disabled = false
        }
      })
    } else {
      min = this.filter.min
      max = this.filter.max
    }
    this.props.handleChange({ min, max })
    this.setState(() => {
      return {
        min,
        max,
      }
    })
  }

  handleAfterChange() {
    const { priceGuidanceFilterData, min, max } = this.state
    if (priceGuidanceFilterData?.length > 0) {
      const updatedPriceGuidance = getUpdatedPriceGuidanceFilter({
        priceGuidanceFilterData,
        min,
        max,
        thisMin: this.filter.min,
        thisMax: this.filter.max,
      })

      this.setState(() => {
        return {
          priceGuidanceFilterData: updatedPriceGuidance,
        }
      })
    }

    if (this.filter.oneWay) {
      this.props.handleChange(this.state.min)
    } else {
      this.props.handleChange(this.state)
    }
  }

  toggleContentModal() {
    this.setState({
      isModalClosed: !this.state.isModalClosed,
    })
  }

  renderAveragePriceSection = () => {
    const { includeSliderContent, includeSliderHeadline, showChip, zIndex } = this.props

    return (
      <React.Fragment>
        {includeSliderContent.altContent ? (
          <IncludeSliderContainer containerPosition={includeSliderContent.position} mt={2}>
            {includeSliderContent.content}
          </IncludeSliderContainer>
        ) : (
          <IncludeSliderContainer
            containerPosition={includeSliderContent.position}
            mt={showChip ? '22px' : 2}
            mb={showChip ? '22px' : 0}
            showChip={showChip}
          >
            {includeSliderHeadline && (
              <StyledPriceGuidanceButton
                variation='link'
                color='primary'
                onClick={this._toggleContentModal}
                data-unit-id='LIGHTBOX_TOGGLE'
                showChip={showChip}
              >
                {includeSliderHeadline}
              </StyledPriceGuidanceButton>
            )}
            <StyledContentBox data-unit-id='LIGHTBOX_WRAPPER' isClosed={this.state.isModalClosed}>
              <LightboxToolTip
                data-unit-id='LIGHTBOX_CLOSE'
                handleLightboxClose={this._toggleContentModal}
                zIndex={zIndex}
                containerWidth={includeSliderContent.width}
                containerMarginLeft={includeSliderContent.marginLeft}
              >
                {' '}
                {includeSliderContent.content}
              </LightboxToolTip>
            </StyledContentBox>
          </IncludeSliderContainer>
        )}
      </React.Fragment>
    )
  }

  renderPriceGuidanceFilterContent = ({ filterName, hideCloseIcon, priceGuidanceFilterData, showChip }) => {
    return priceGuidanceFilterData.map((item) => {
      const budgetPrimaryLabel =
        item.id === `${filterName}-price-guidance-4`
          ? `+ $${item.min}`
          : `$${item.min} - $${item.max}
    `
      item.primaryLabel = `${budgetPrimaryLabel} per night`
      return showChip ? (
        <Box key={item.id} isDisabled={item.disabled} mx={1}>
          <ChipFilter filter={item} chipId={item.id} data-unit-id={item.id} hideCloseIcon={hideCloseIcon} />
        </Box>
      ) : (
        <CheckBoxFilterBox key={item.id} isDisabled={item.disabled}>
          <CheckboxFilter
            primaryLabel={
              this.props.priceGuidanceNewLabelStyle ? (
                this.generateStyledLabel(item.label)
              ) : (
                <StyledStamp color='pricePrimary'>{item.label}</StyledStamp>
              )
            }
            secondaryLabel={item.msg}
            title={item.label}
            checkboxId={item.id}
            checked={item.checked}
            data-unit-id={item.id}
            min={item.min}
            max={item.max}
            disabled={item.disabled}
          />
        </CheckBoxFilterBox>
      )
    })
  }

  render() {
    const { priceGuidanceFilterData } = this.state
    const {
      avgRetailPrice,
      filterName,
      hideCloseIcon,
      includeSliderContent,
      markedValues,
      priceGuidanceAllStarAvgPrice,
      showChip,
      showSliderInputs,
      theme,
      hideCheckboxes,
      sliderInputMaxLabel,
      sliderInputMinLabel,
    } = this.props
    const perNightLabel = priceGuidanceAllStarAvgPrice ? ' per night' : undefined
    const showChipLabel = avgRetailPrice ? `${avgRetailPrice} avg. per night` : undefined
    const valueTextFormatter = getValueTextFormatter(this.filter)
    const ariaLabels = getAriaLabels(this.filter, filterName)

    return (
      <SliderCategoryGroupWrapper>
        <RangeSliderLabel
          formatByTime={this.filter.formatByTime}
          formatByDuration={this.filter.formatByDuration}
          currencySymbol={this.filter.currencySymbol}
          minSelected={this.state.min}
          maxSelected={this.state.max}
          absMax={this.filter.max}
          absMin={this.filter.min}
          plusSymbol={this.filter.plusSymbol}
          sliderName={this.filter.name}
          sliderIcon={this.filter.sliderIcon}
          isMilitaryTime={this.filter.isMilitaryTime}
          postFixText={perNightLabel}
          utc={this.filter.utc}
        />
        {showChip && avgRetailPrice && (
          <Label fontSize={0} fontWeight='regular' color='text'>
            {this.filter.currencySymbol}
            <Text.span>{showChipLabel}</Text.span>
          </Label>
        )}
        <RangeSlider
          oneWay={this.filter.oneWay}
          defaultValueArray={this.filter.defaultValueArray}
          value={this.filter.value || [this.state.min, this.state.max]}
          min={this.filter.min}
          max={this.filter.max}
          handleChange={this._handleChange}
          handleAfterChange={this._handleAfterChange}
          disabled={this.filter.disabled}
          step={this.filter.step}
          theme={theme}
          markedValues={markedValues}
          ariaLabelGroupForHandles={this.filter.ariaLabelGroupForHandles || ariaLabels}
          ariaLabelledByGroupForHandles={this.filter.ariaLabelledByGroupForHandles}
          ariaValueTextFormattersForHandles={
            this.filter.ariaValueTextFormattersForHandles || [valueTextFormatter, valueTextFormatter]
          }
          ariaLabelForHandle={this.filter.ariaLabelForHandle || ariaLabels[0]}
          ariaLabelledByForHandle={this.filter.ariaLabelledByForHandle}
          ariaValueTextFormatterForHandle={this.filter.ariaValueTextFormatterForHandle || valueTextFormatter}
        />
        {showSliderInputs && (
          <RangeSliderNumberInput
            absoluteMax={this.filter.max}
            absoluteMin={this.filter.min}
            labelMax={sliderInputMaxLabel}
            labelMin={sliderInputMinLabel}
            max={this.state.max}
            min={this.state.min}
            onChange={this._handleInputChange}
          />
        )}
        {markedValues?.description && (
          <Text fontSize={0} color='text.light' mt={showSliderInputs ? 1 : 3} pt={2}>
            {markedValues.description.a11yPrefix && (
              <SrOnly>{markedValues.description.a11yPrefix} &nbsp;</SrOnly>
            )}
            {markedValues.description.text}
            {markedValues.description.a11yPostfix && (
              <SrOnly>&nbsp; {markedValues.description.a11yPostfix}</SrOnly>
            )}
          </Text>
        )}
        {showChip && includeSliderContent && this.renderAveragePriceSection()}
        {!hideCheckboxes && priceGuidanceAllStarAvgPrice > 0 && priceGuidanceFilterData?.length > 0 && (
          <FilterOptionsBox
            data-unit-id='price-guidance-checkbox'
            mt={2}
            onChange={(e) => this.onChange(e)}
            showChip={showChip}
          >
            {this.renderPriceGuidanceFilterContent({
              filterName,
              hideCloseIcon,
              priceGuidanceFilterData,
              showChip,
            })}
          </FilterOptionsBox>
        )}
        {!showChip && includeSliderContent && this.renderAveragePriceSection()}
      </SliderCategoryGroupWrapper>
    )
  }
}

SliderCategoryGroup.propTypes = propTypes
SliderCategoryGroup.defaultProps = {
  hideCloseIcon: false,
  includeSliderContent: {
    altContent: false,
    marginLeft: '70px',
    width: '175px',
  },
  includeSliderHeadline: '',
  sliderInputMaxLabel: 'Maximum Price',
  sliderInputMinLabel: 'Minimum Price',
  zIndex: 1000,
}

export default SliderCategoryGroup
